import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, CircleMarker, Circle, Popup, Marker } from 'react-leaflet';
import L from 'leaflet';
import { Phone, MapPin, Ambulance, Car, ArrowRight, Navigation } from 'lucide-react';
import { Reveal } from '@/components/ui/Reveal';
import { Button } from '@/components/ui/button';
import { useSEO, buildBreadcrumb, SITE_URL } from '@/lib/seo';
import api from '@/lib/api';

// Map implantation slugs to their dedicated landing pages
const IMPLANTATION_LANDING_PAGES = {
  'garnat-sur-engievre': { path: '/ambulance-beaulon-garnat', label: 'Voir la page dédiée Ambulances Beaulon' },
  'avermes-saint-ennemond': { path: '/ambulance-03-avermes', label: 'Voir la page dédiée Ambulance 03' },
  'tronget': { path: '/ambulance-tronget-le-montet', label: 'Voir la page dédiée Ambulance Tronget' },
  'diou': { path: '/celine-ambulance-diou', label: 'Voir la page dédiée Céline Ambulance' },
};

// Custom marker icon (Leaflet default needs manual setup)
const makeIcon = (color = '#1F7AAE') =>
  L.divIcon({
    className: '',
    html: `<div style="position:relative">
      <div style="position:absolute;inset:-10px;border-radius:9999px;background:${color};opacity:.18;animation:pulse-ring 2.5s cubic-bezier(0.215, 0.61, 0.355, 1) infinite"></div>
      <div style="width:22px;height:22px;border-radius:9999px;background:${color};border:3px solid white;box-shadow:0 6px 16px rgba(15,23,32,0.25);"></div>
    </div>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  });

export default function Implantations() {
  useSEO({
    title: "Nos implantations dans l'Allier | Groupe Ambulances Beaulon",
    description: "Découvrez nos 6 implantations dans l'Allier : Garnat-sur-Engièvre, Diou, Tronget, Avermes Saint-Ennemond, Noyant-d'Allier, Cressanges. Carte interactive, contacts et pages locales dédiées.",
    keywords: "ambulance garnat sur engievre, ambulance diou, ambulance tronget, ambulance avermes, ambulance saint ennemond, ambulance noyant allier, ambulance cressanges, implantations ambulance allier, agences ambulance allier",
    canonical: `${SITE_URL}/implantations`,
    jsonLd: [
      buildBreadcrumb([
        { name: 'Accueil', url: SITE_URL + '/' },
        { name: 'Nos implantations', url: SITE_URL + '/implantations' },
      ]),
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: 'Implantations du Groupe Ambulances Beaulon dans l\'Allier',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Ambulances Beaulon – Garnat-sur-Engièvre', url: `${SITE_URL}/ambulance-beaulon-garnat` },
          { '@type': 'ListItem', position: 2, name: 'Ambulance 03 – Avermes / Saint-Ennemond', url: `${SITE_URL}/ambulance-03-avermes` },
          { '@type': 'ListItem', position: 3, name: 'Céline Ambulance – Diou', url: `${SITE_URL}/celine-ambulance-diou` },
          { '@type': 'ListItem', position: 4, name: 'Ambulance Tronget – Le Montet', url: `${SITE_URL}/ambulance-tronget-le-montet` },
        ],
      },
    ],
  });

  const [implantations, setImplantations] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get('/implantations').then(r => setImplantations(r.data)).catch(() => {});
  }, []);

  const center = useMemo(() => [46.52, 3.30], []);

  return (
    <div>
      <section className="map-wash">
        <div className="container-app pt-12 pb-8 lg:pt-16 lg:pb-10">
          <Reveal>
            <p className="badge-soft mb-3">Nos implantations</p>
            <h1 className="h1 text-anthracite-950">Une couverture territoriale fine, partout dans l'Allier</h1>
            <p className="lead mt-4 max-w-3xl">Nos 6 implantations nous permettent d'être proches de nos patients et de répondre rapidement à vos besoins de transport sanitaire.</p>
          </Reveal>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-app">
          <div className="grid lg:grid-cols-[1fr_1.4fr] gap-6">
            {/* List */}
            <div className="space-y-3 order-2 lg:order-1">
              {implantations.map((i) => (
                <button
                  key={i.id}
                  type="button"
                  onClick={() => setActive(i.id)}
                  className={`w-full text-left card-base p-4 transition-all border ${
                    active === i.id ? 'border-brand-500 ring-2 ring-brand-200' : 'border-border'
                  }`}
                  data-testid="implantation-list-item"
                >
                  <div className="flex items-start gap-3">
                    <div className={`mt-1 h-9 w-9 rounded-lg flex items-center justify-center ${i.is_main ? 'bg-brand-500 text-white' : 'bg-brand-50 text-brand-700'}`}>
                      <MapPin className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <h3 className="font-heading font-semibold text-anthracite-950">{i.name}</h3>
                        {i.is_main && <span className="text-[10px] font-medium uppercase tracking-wide text-brand-700 bg-brand-50 px-2 py-0.5 rounded">Principal</span>}
                      </div>
                      {i.address && <p className="text-xs text-muted-foreground mt-0.5">{i.address}</p>}
                      <div className="mt-3 flex flex-wrap gap-2 text-xs">
                        {i.ambulances > 0 && <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-anthracite-100 text-anthracite-700"><Ambulance className="h-3 w-3" /> {i.ambulances} amb.</span>}
                        {i.vsl > 0 && <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-anthracite-100 text-anthracite-700"><Car className="h-3 w-3" /> {i.vsl} VSL</span>}
                        {i.taxis > 0 && <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-anthracite-100 text-anthracite-700"><Car className="h-3 w-3" /> {i.taxis} taxi{i.taxis > 1 ? 's' : ''}</span>}
                      </div>
                      {i.phone && (
                        <a href={`tel:${i.phone.replace(/\s/g, '')}`} className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-brand-700 hover:text-brand-800" data-testid={`implantation-phone-${i.slug}`}>
                          <Phone className="h-3.5 w-3.5" /> {i.phone}
                        </a>
                      )}
                      {IMPLANTATION_LANDING_PAGES[i.slug] && (
                        <Link
                          to={IMPLANTATION_LANDING_PAGES[i.slug].path}
                          onClick={(e) => e.stopPropagation()}
                          className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-brand-700 hover:text-brand-900 bg-brand-50 border border-brand-100 rounded-full px-2.5 py-1"
                          data-testid={`implantation-landing-${i.slug}`}
                        >
                          {IMPLANTATION_LANDING_PAGES[i.slug].label} <ArrowRight className="h-3 w-3" />
                        </Link>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Map */}
            <div className="order-1 lg:order-2">
              <div className="rounded-2xl overflow-hidden border bg-white shadow-sm h-[420px] sm:h-[520px] lg:h-[640px]" data-testid="implantations-leaflet-map">
                <MapContainer center={center} zoom={9} scrollWheelZoom={false} style={{ height: '100%', width: '100%' }}>
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  {implantations.map((i) => (
                    <Circle
                      key={`c-${i.id}`}
                      center={[i.lat, i.lng]}
                      radius={i.radius_km * 1000}
                      pathOptions={{
                        color: '#1F7AAE',
                        fillColor: '#1F7AAE',
                        weight: active === i.id ? 2 : 1,
                        opacity: active === i.id ? 0.6 : 0.35,
                        fillOpacity: active === i.id ? 0.22 : 0.12,
                      }}
                    />
                  ))}
                  {implantations.map((i) => (
                    <Marker key={i.id} position={[i.lat, i.lng]} icon={makeIcon(i.is_main ? '#0E3A57' : '#1F7AAE')} eventHandlers={{ click: () => setActive(i.id) }}>
                      <Popup>
                        <div className="min-w-[220px]">
                          <div className="font-heading font-semibold text-anthracite-950">{i.name}</div>
                          {i.address && <div className="text-xs text-muted-foreground mt-0.5">{i.address}</div>}
                          <div className="flex flex-wrap gap-1 mt-2 text-xs">
                            {i.ambulances > 0 && <span className="px-2 py-0.5 rounded bg-anthracite-100">{i.ambulances} amb.</span>}
                            {i.vsl > 0 && <span className="px-2 py-0.5 rounded bg-anthracite-100">{i.vsl} VSL</span>}
                            {i.taxis > 0 && <span className="px-2 py-0.5 rounded bg-anthracite-100">{i.taxis} taxi{i.taxis > 1 ? 's' : ''}</span>}
                          </div>
                          {i.phone && (
                            <a href={`tel:${i.phone.replace(/\s/g, '')}`} className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-brand-700">
                              <Phone className="h-3.5 w-3.5" /> {i.phone}
                            </a>
                          )}
                          <a
                            href={`https://www.google.com/maps/dir/?api=1&destination=${i.lat},${i.lng}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-2 inline-flex items-center gap-1.5 text-xs text-brand-700 hover:text-brand-800"
                          >
                            <Navigation className="h-3 w-3" /> Itinéraire
                          </a>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              </div>
            </div>
          </div>

          {/* CTA */}
          <Reveal delay={100}>
            <div className="mt-10 rounded-2xl border bg-brand-50/60 p-6 sm:p-8 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <div>
                <h3 className="h3 text-anthracite-950">Besoin d'un transport ?</h3>
                <p className="text-sm text-anthracite-700 mt-1">Contactez l'implantation la plus proche, demandez un devis, ou estimez le coût de votre trajet.</p>
              </div>
              <div className="flex gap-3 flex-wrap">
                <a href="tel:0470427263" className="inline-flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2.5 text-white font-medium shadow-btn hover:bg-brand-600"><Phone className="h-4 w-4" /> Appeler</a>
                <Link to="/estimation-transport" className="inline-flex items-center gap-2 rounded-lg bg-white border-2 border-brand-500 text-brand-700 hover:bg-brand-50 px-4 py-2.5 font-medium" data-testid="implantations-estimation-cta">
                  Estimer mon transport <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/devis" className="inline-flex items-center gap-2 rounded-lg bg-white border px-4 py-2.5 font-medium">Demander un devis <ArrowRight className="h-4 w-4" /></Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
